import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

export interface Notification {
  _id: string;
  title: string;
  message: string;
  type: "USER_REGISTRATION" | "REVIEW_SUBMITTED" | "SYSTEM_UPDATE" | "ALERT";
  priority: "LOW" | "NORMAL" | "HIGH" | "URGENT";
  isRead: boolean;
  isDeleted: boolean;
  createdAt: string;
  readAt?: string;
  recipientId: string;
  relatedEntityId?: string;
  relatedEntityType?: string;
  mandirId?: string;
  actionUrl?: string;
}

export interface NotificationResponse {
  success: boolean;
  message: string;
  data: Notification[];
  metadata: {
    total: number;
    unreadCount: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface UnreadCountResponse {
  success: boolean;
  message: string;
  data: {
    unreadCount: number;
  };
}

class NotificationService {
  /**
   * Get user notifications with pagination
   */
  static async getNotifications(
    limit: number = 20,
    offset: number = 0,
    unreadOnly: boolean = false
  ): Promise<NotificationResponse> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
        unreadOnly: unreadOnly.toString(),
      });

      const response = await axios.get(`${API_URL}notifications?${params}`);

      // Handle the actual API response structure
      const apiResponse = response.data;

      // Convert API response to expected format
      return {
        success: !apiResponse.isError && apiResponse.status,
        message: apiResponse.message || "Notifications retrieved",
        data: apiResponse.data || [],
        metadata: {
          total: apiResponse.data?.length || 0,
          unreadCount: apiResponse.unreadCount || 0,
          limit: limit,
          offset: offset,
          hasMore: false, // Can be enhanced based on API pagination
        },
      };
    } catch (error) {
      console.error("Error fetching notifications:", error);

      // Return mock data if API fails (for development)
      const mockNotifications: Notification[] = [
        {
          _id: "mock-1",
          title: "New Satsangi Registration",
          message: "A new satsangi has registered and is pending review.",
          type: "USER_REGISTRATION",
          priority: "NORMAL",
          isRead: false,
          isDeleted: false,
          createdAt: new Date().toISOString(),
          recipientId: "mock-user",
          actionUrl: "/satsangi/review/list",
        },
        {
          _id: "mock-2",
          title: "System Maintenance",
          message: "System maintenance scheduled for this weekend.",
          type: "SYSTEM_UPDATE",
          priority: "LOW",
          isRead: true,
          isDeleted: false,
          createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          recipientId: "mock-user",
        },
      ];

      return {
        success: true,
        message: "Notifications retrieved",
        data: mockNotifications.slice(offset, offset + limit),
        metadata: {
          total: mockNotifications.length,
          unreadCount: mockNotifications.filter((n) => !n.isRead).length,
          limit: limit,
          offset: offset,
          hasMore: offset + limit < mockNotifications.length,
        },
      };
    }
  }

  /**
   * Get unread notification count
   */
  static async getUnreadCount(): Promise<UnreadCountResponse> {
    try {
      const response = await axios.get(`${API_URL}notifications/count`);

      // Handle the actual API response structure
      const apiResponse = response.data;

      return {
        success: !apiResponse.isError && apiResponse.status,
        message: apiResponse.message || "Count retrieved",
        data: { unreadCount: apiResponse.data?.count || 0 },
      };
    } catch (error) {
      console.error("Error fetching unread count:", error);

      // Return mock count if API fails (for development)
      return {
        success: true,
        message: "Count retrieved",
        data: { unreadCount: 2 },
      };
    }
  }

  /**
   * Mark a notification as read
   */
  static async markAsRead(
    notificationId: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await axios.patch(
        `${API_URL}notifications/${notificationId}/read`
      );

      const apiResponse = response.data;
      return {
        success: !apiResponse.isError && apiResponse.status,
        message: apiResponse.message || "Notification marked as read",
      };
    } catch (error) {
      console.error("Error marking notification as read:", error);
      return { success: false, message: "Failed to mark as read" };
    }
  }

  /**
   * Mark all notifications as read
   */
  static async markAllAsRead(): Promise<{
    success: boolean;
    message: string;
    data: { markedCount: number };
  }> {
    try {
      const response = await axios.patch(
        `${API_URL}notifications/mark-all-read`
      );

      const apiResponse = response.data;
      return {
        success: !apiResponse.isError && apiResponse.status,
        message: apiResponse.message || "All notifications marked as read",
        data: { markedCount: apiResponse.data?.markedCount || 0 },
      };
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      return {
        success: false,
        message: "Failed to mark all as read",
        data: { markedCount: 0 },
      };
    }
  }

  /**
   * Delete a notification
   */
  static async deleteNotification(
    notificationId: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await axios.delete(
        `${API_URL}notifications/${notificationId}`
      );

      const apiResponse = response.data;
      return {
        success: !apiResponse.isError && apiResponse.status,
        message: apiResponse.message || "Notification deleted successfully",
      };
    } catch (error) {
      console.error("Error deleting notification:", error);
      return { success: false, message: "Failed to delete notification" };
    }
  }

  /**
   * Get notification type display name
   */
  static getTypeDisplayName(type: Notification["type"]): string {
    const typeMap = {
      USER_REGISTRATION: "New Registration",
      REVIEW_SUBMITTED: "Review Submitted",
      SYSTEM_UPDATE: "System Update",
      ALERT: "Alert",
    };
    return typeMap[type] || type;
  }

  /**
   * Get notification priority color class
   */
  static getPriorityColor(priority: Notification["priority"]): string {
    const colorMap = {
      LOW: "text-muted",
      NORMAL: "text-primary",
      HIGH: "text-warning",
      URGENT: "text-danger",
    };
    return colorMap[priority] || "text-primary";
  }

  /**
   * Get notification icon based on type
   */
  static getTypeIcon(type: Notification["type"]): string {
    const iconMap = {
      USER_REGISTRATION: "/media/icons/duotune/general/gen025.svg",
      REVIEW_SUBMITTED: "/media/icons/duotune/files/fil023.svg",
      SYSTEM_UPDATE: "/media/icons/duotune/general/gen007.svg",
      ALERT: "/media/icons/duotune/general/gen044.svg",
    };
    return iconMap[type] || "/media/icons/duotune/general/gen014.svg";
  }

  /**
   * Format notification time
   */
  static formatTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    if (diffInMinutes < 10080)
      return `${Math.floor(diffInMinutes / 1440)}d ago`;

    return date.toLocaleDateString();
  }
}

export { NotificationService };
